import { call, put, select } from 'redux-saga/effects';
import { websocketOpenSubscriptionById } from '../../websocketReducer';
import { getLiteTradeConfig, getLiteTradeSessionToken } from '../../../../modules/LiteTrade/liteTradeSelectors';
import { getIsAppVisibility, getUserSelectedAccountCode } from '../../../../app/appSelectors';
import { IMobileAppRootState } from '../../../../mobileAppCore/types/mobileAppState.t';
import { oppositeSubscriptionName, SOCKET_TYPE_MESSAGES } from '../../../../mobileAppCore/mobileAppConstants';
import LiteTradeNotification
    from '../../../../modules/LiteTrade/components/LiteTradeNotification/LiteTradeNotification';
import LiteTradeNotificationCloseButton
    from '../../../../modules/LiteTrade/components/LiteTradeNotification/LiteTradeNotificationCloseButton';
import { LITE_TRADE_ACCOUNT_EVENT_TYPES } from '../../../../modules/LiteTrade/liteTradeConstants';
import { openNotificationAction } from '../../../../modules/Notifications/notificationsReducer';
import parseAccountName from '../../../../utils/parseAccountName';
import { getWebsocketSubscriptionById } from '../../websocketSelectors';
import _ from 'lodash';
import { makeSelectLangPath } from '../../../LangProvider/langProviderSelectors';
import { IAccountEvent } from '../../../../modules/LiteTrade/types/liteTradeReducer.t';
import {
    TLiteTradeNotificationType,
} from '../../../../modules/LiteTrade/components/LiteTradeNotification/liteTradeNotification.t';
import {
    LITE_TRADE_NOTIFICATION_TYPES
} from '../../../../modules/LiteTrade/components/LiteTradeNotification/liteTradeNotificationConstants';

function* openAccountEventNotificationSaga({ payload }: { payload: IAccountEvent }) {
    const accountType = parseAccountName(payload.account)?.type;
    const notificationType = _.toLower(payload.eventType) as TLiteTradeNotificationType;
    const description = yield select(makeSelectLangPath(
        `lite_version.notifications.description.${notificationType}`,
        { actual_risk_level: `${payload.actualRiskLevel}` },
    ));
    yield put(
        openNotificationAction({
            content: (
                <LiteTradeNotification
                    accountType={accountType}
                    iconName={notificationType === LITE_TRADE_NOTIFICATION_TYPES.LIQUIDATION
                        ? 'IcMeterStopOut'
                        : 'IcMeterMarginCall'
                    }
                    description={description}
                    type={notificationType}
                />
            ),
            options: {
                autoClose: false,
                closeButton: LiteTradeNotificationCloseButton,
            },
        }),
    );
}

function* subscriptionAccountEventsSaga() {
    const config = yield select(getLiteTradeConfig);
    const channel = config?.wssUrl;
    const type = SOCKET_TYPE_MESSAGES.ACCOUNT_EVENTS_OPEN;
    const requestId = `${SOCKET_TYPE_MESSAGES.ACCOUNT_EVENTS_OPEN}-AccountEvents`;
    const selectedAccountCode = yield select(getUserSelectedAccountCode);
    const sessionToken = yield select(getLiteTradeSessionToken);
    const isAppVisibility = yield select(getIsAppVisibility);
    const lastSubscription = yield select(getWebsocketSubscriptionById(channel, requestId));
    const accountCodes = Array.isArray(selectedAccountCode) ? selectedAccountCode : [selectedAccountCode];

    if (lastSubscription?.prev?.accountCodes && (lastSubscription?.state === 'open' || lastSubscription?.state === 'opening')) {
        const prevAccountCodes = lastSubscription.prev.accountCodes;
        if (_.isEqual(prevAccountCodes, accountCodes)) {
            return;
        }
    }

    if (selectedAccountCode && isAppVisibility && sessionToken && channel) {
        yield put(
            websocketOpenSubscriptionById({
                id: requestId,
                channel,
                prev: {
                    accountCodes,
                },
                open: function(state: IMobileAppRootState) {
                    const sessionToken = getLiteTradeSessionToken(state);
                    const selectedAccountCode = getUserSelectedAccountCode(state);
                    const accountCodes = Array.isArray(selectedAccountCode)
                        ? selectedAccountCode
                        : [selectedAccountCode];

                    return {
                        requestId,
                        type,
                        dataType: 'AccountEvents',
                        payload: {
                            accounts: accountCodes.map((accountCode) => `default:${accountCode}`),
                            requestType: 'LIST',
                        },
                        session: sessionToken,
                    };
                },
                process: function* (messageData: ReturnType<typeof JSON.parse>) {
                    const accountEvents: IAccountEvent[] = messageData.payload.accountEvents;
                    if (!Array.isArray(accountEvents)) {
                        return;
                    }
                    for (let i = 0; i < accountEvents.length; i++) {
                        const accountEvent = accountEvents[i];
                        if (!!LITE_TRADE_ACCOUNT_EVENT_TYPES[accountEvent.eventType]) {
                            yield call(openAccountEventNotificationSaga, { payload: accountEvent });
                        }
                    }
                },
                close: function(state: IMobileAppRootState) {
                    const sessionToken = getLiteTradeSessionToken(state);
                    return {
                        type: oppositeSubscriptionName[type],
                        refRequestId: requestId,
                        session: sessionToken,
                    };
                },
            }),
        );
    }
}

export default subscriptionAccountEventsSaga;
